import { observer }            from 'mobx-react-lite';
import BadgeSettings           from '@/TokamapEditor/ux/AreaSidebar/BadgeSettings';
import currentEditorStore      from '@/TokamapEditor/stores/currentEditorStore';
import { commonValue }         from '@/TokamapEditor/ux/AreaSidebar/multiSelectHelpers';
import { badgeEligibleShapes } from '@/TokamapEditor/helpers/shapeBadge';
import { effectiveBadge }      from '@/TokamapEditor/helpers/shapeBadge';
import { badgeFieldLock }      from '@/TokamapEditor/helpers/shapeBadge';
import { textPositionOf }      from '@/TokamapEditor/helpers/shapeBadge';
import HelpTip                 from '@/TokamapEditor/ux/AreaSidebar/HelpTip';
import View                    from '@widesk-ui/components/View';
import Segmented               from '@widesk-ui/Segmented';
import SectionTitle            from '@/TokamapEditor/ux/AreaSidebar/SectionTitle';
import SettingsButton          from '@/TokamapEditor/ux/AreaSidebar/SettingsPopover';
import useDesignTokens         from '@/hooks/useDesignTokens';
import { ThemeLockTag }        from '@/TokamapEditor/ux/AreaSidebar/ThemeLocked';
import { ThemeLockedControl }  from '@/TokamapEditor/ux/AreaSidebar/ThemeLocked';
import { zoomFactorLabel }     from '@/TokamapEditor/helpers/zoomAppearance';
import type { ShapeBadge }     from '@tokamap/core';

export const BADGE_MODES_HELP = `Dans la forme : à son échelle, ils tournent avec la carte. En marqueur : au-dessus de la forme, à l'endroit et de la même taille à l'écran à tous les zooms.`;

const STYLE_LABELS: Record<NonNullable<ShapeBadge['style']>, string> = { disc: 'Pastille', pin: 'Épingle', tag: 'Étiquette' };
const SIZE_LABELS: Record<NonNullable<ShapeBadge['size']>, string> = { small: 'Petit', medium: 'Moyen', large: 'Grand' };
const SHAPE_LABELS: Record<NonNullable<ShapeBadge['shape']>, string> = { round: 'Rond', square: 'Carré' };

const BadgeDisplayField = observer(() => {
	const editor = currentEditorStore.get;
	const tokens = useDesignTokens();

	const shapes = badgeEligibleShapes(currentEditorStore.sidebarShapes);

	if (!shapes.length) return null;

	const ids = shapes.map(shape => shape.id);
	const standalone = shapes.every(shape => shape.shapeKind === 'marker');
	const badge = shapes[0]!.badge ?? {};
	const update = (patch: Partial<ShapeBadge>) => editor.setShapeBadge(ids, { ...badge, ...withoutDefaults(patch) });

	const modeLock = badgeFieldLock(shapes);
	const locks = {
		style: badgeFieldLock(shapes, 'style'),
		size: badgeFieldLock(shapes, 'size'),
		shape: badgeFieldLock(shapes, 'shape'),
		color: badgeFieldLock(shapes, 'color'),
		anchor: badgeFieldLock(shapes, 'anchor'),
		showText: badgeFieldLock(shapes, 'showText'),
		textPosition: badgeFieldLock(shapes, 'textPosition'),
		minZoomOffset: badgeFieldLock(shapes, 'minZoomOffset'),
	};
	const mode = commonValue(shapes, shape => (effectiveBadge(shape) ? 'marker' : 'inside'));
	const values = {
		style: commonValue(shapes, shape => effectiveBadge(shape)?.style ?? 'disc'),
		size: commonValue(shapes, shape => effectiveBadge(shape)?.size ?? 'medium'),
		shape: commonValue(shapes, shape => effectiveBadge(shape)?.shape ?? 'round'),
		anchor: commonValue(shapes, shape => effectiveBadge(shape)?.anchor ?? 'center'),
		showText: commonValue(shapes, shape => effectiveBadge(shape)?.showText ?? true),
		textPosition: commonValue(shapes, shape => textPositionOf(effectiveBadge(shape))),
		color: commonValue(shapes, shape => effectiveBadge(shape)?.color),
		colorMixed: new Set(shapes.map(shape => effectiveBadge(shape)?.color)).size > 1,
		minZoomOffset: commonValue(shapes, shape => effectiveBadge(shape)?.minZoomOffset),
		minZoomOffsetMixed: new Set(shapes.map(shape => effectiveBadge(shape)?.minZoomOffset)).size > 1,
	};

	const mixed = !values.style || !values.size || !values.shape;
	const summary = mixed ? 'Plusieurs valeurs' : [STYLE_LABELS[values.style!], SIZE_LABELS[values.size!], SHAPE_LABELS[values.shape!]].join(' · ');
	const details = [
		values.showText === 'hover' ? 'texte au survol' : values.showText === false ? 'texte masqué' : undefined,
		values.minZoomOffset ? `dès le zoom ${zoomFactorLabel(values.minZoomOffset)}` : undefined,
	].filter((detail): detail is string => !!detail);
	const color = values.color ?? commonValue(shapes, shape => shape.style?.bgColor) ?? tokens.color.primary;

	return (
		<View gap="sm">
			<View row spread centerV>
				<SectionTitle>Affichage</SectionTitle>
				{!!modeLock && !standalone && <ThemeLockTag lock={modeLock} />}
			</View>

			{!standalone && (
				<ThemeLockedControl lock={modeLock}>
					<Segmented
						block
						value={mode}
						onChange={value => editor.setShapeBadge(ids, value === 'marker' ? {} : undefined)}
						options={[
							{ value: 'inside', label: 'Dans la forme' },
							{ value: 'marker', label: 'En marqueur' },
						]}
					/>
				</ThemeLockedControl>
			)}

			{!standalone && mode === 'inside' && (
				<View size={12} color={tokens.text.muted}>À l'échelle de la forme, l'image et le texte tournent avec la carte.</View>
			)}

			{(standalone || mode === 'marker') && (
				<SettingsButton
					width={300}
					label="Marqueur"
					title={<>Marqueur<HelpTip>{BADGE_MODES_HELP}</HelpTip></>}
					summary={summary}
					details={details}
					mixed={mixed}
					themed={Object.values(locks).some(Boolean)}
					preview={(
						<span
							style={{
								width: 24,
								height: 24,
								borderRadius: values.shape === 'square' ? 6 : '50%',
								flexShrink: 0,
								background: color,
								boxShadow: `inset 0 0 0 3px ${tokens.surface.elevated}, 0 0 0 1px ${tokens.border.default}`,
							}}
						/>
					)}
				>
					<BadgeSettings
						withoutMode
						label="Marqueur"
						standalone={standalone}
						modeLockedBy={modeLock}
						locks={locks}
						modes={['Dans la forme', 'En marqueur']}
						mode="marker"
						values={values}
						onMode={() => undefined}
						onChange={update}
					/>
				</SettingsButton>
			)}
		</View>
	);
});

export default BadgeDisplayField;

/** Une forme n'enregistre que ce qui s'écarte du marqueur par défaut. */
function withoutDefaults(patch: Partial<ShapeBadge>): Partial<ShapeBadge> {
	return {
		...patch,
		...(patch.size === 'medium' ? { size: undefined } : {}),
		...(patch.shape === 'round' ? { shape: undefined } : {}),
		...(patch.showText === true ? { showText: undefined } : {}),
	};
}
